import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import MessageCard from "../components/MessageCard";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import SubscribeModal from "../components/SubscribeModal";

const API_BASE = process.env.REACT_APP_API_URL || "";

function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState(null);

  // The tab list comes from the server, so adding a group there adds a tab here.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/channels`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d.channels?.length) return;
        setChannels(d.channels);
        setChannel((c) => c || d.channels[0].key);
      })
      .catch(() => {
        // Tabs are navigation, not content — the default channel still loads.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ch = channel ? `&channel=${encodeURIComponent(channel)}` : "";
      const url = searchQuery.trim()
        ? `${API_BASE}/api/messages/search?q=${encodeURIComponent(searchQuery)}${ch}`
        : `${API_BASE}/api/messages?page=${page}&limit=30${ch}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load messages");
      const data = await res.json();

      setMessages(data.messages || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, channel]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Switching tabs restarts paging: page 4 of one group means nothing in another.
  const selectChannel = (key) => {
    if (key === channel) return;
    setChannel(key);
    setPage(1);
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    setPage(1);
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="header-title-row">
            <div className="logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <h1>Telegram Chat Viewer</h1>
              <p className="header-subtitle">
                {total > 0 ? `${total} הודעות` : "טוען..."}
                {lastRefresh && (
                  <span className="refresh-time">
                    {" "}· עדכון אחרון {lastRefresh.toLocaleTimeString("he-IL")}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <SearchBar onSearch={handleSearch} />
            <Link to="/stocks" className="nav-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
              <span>מעקב מניות</span>
            </Link>
            <button className="subscribe-btn" onClick={() => setShowSubscribe(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span>התראות</span>
            </button>
          </div>
        </div>
      </header>

      <main className="main">
        {channels.length > 1 && (
          <div className="stock-tabs" role="tablist">
            {channels.map((c) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={channel === c.key}
                className={`stock-tab${channel === c.key ? " active" : ""}`}
                onClick={() => selectChannel(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="error-banner">
            <span>⚠ {error}</span>
            <button onClick={fetchMessages}>נסה שוב</button>
          </div>
        )}

        {loading && messages.length === 0 ? (
          <div className="loader-container">
            <div className="loader" />
            <p>טוען הודעות...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <line x1="9" y1="9" x2="15" y2="9" />
              <line x1="9" y1="13" x2="13" y2="13" />
            </svg>
            <h2>{searchQuery ? "לא נמצאו תוצאות" : "אין הודעות עדיין"}</h2>
            <p>{searchQuery ? "נסה חיפוש אחר" : "הודעות יופיעו כאן ברגע שהסקריפט ירוץ"}</p>
          </div>
        ) : (
          <>
            <div className="messages-grid">
              {messages.map((msg) => (
                <MessageCard key={msg._id || msg.message_id} message={msg} />
              ))}
            </div>
            {!searchQuery && pages > 1 && (
              <Pagination page={page} pages={pages} onPageChange={setPage} />
            )}
          </>
        )}
      </main>

      <footer className="footer">
        <p>Telegram Chat Viewer · Powered by Telethon</p>
      </footer>

      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} />}
    </div>
  );
}

export default MessagesPage;
